import { useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectAuthToken } from 'selectors';
import type { AxiosResponse } from 'axios';
import { useNotification } from 'hooks/useNotification';
import {
  fetchReplicaCredentialsApi,
  FetchReplicaCredentialsParams,
  FetchReplicaCredentialsResponse,
} from '../fetchReplicaCredentialsApi';

/**
 * Mutation hook to fetch replica credentials on demand.
 * Uses a mutation (not a query) because this is triggered by a user action, not on mount.
 */
export const useFetchReplicaCredentialsMutation = () => {
  const token = useSelector(selectAuthToken) as string | undefined;
  const { showSuccess, showError } = useNotification();

  const mutation = useMutation<
    AxiosResponse<FetchReplicaCredentialsResponse>,
    unknown,
    FetchReplicaCredentialsParams
  >({
    mutationFn: async (params) => {
      if (!token) throw new Error('Not Authenticated');
      return fetchReplicaCredentialsApi({ params, token });
    },
    onSuccess: (response) => {
      if (response.data.exists) {
        showSuccess('Replica credentials exist and are ready to use');
      } else {
        showSuccess(
          'No existing replica credentials found. You may need to provision new credentials.',
        );
      }
    },
    onError: () => {
      showError('Failed to fetch replica credentials');
    },
  });

  return {
    fetchReplicaCredentials: mutation.mutate,
    fetchReplicaCredentialsAsync: mutation.mutateAsync,
    isFetchingReplicaCredentials: mutation.isPending,
    credentialsExist: mutation.data?.data?.exists ?? null,
  };
};
